<script setup>
/**
 * @description 下载中心资料详情页
 * @reason 沿用原下载详情的信息层级与版式，同时使用路径参数和统一的资源预览能力。
 */

import { computed, onMounted, ref, watchEffect } from 'vue'
import {
  IconClockCircle,
  IconDownload,
  IconEye,
  IconShareInternal,
} from '@arco-design/web-vue/es/icon'
import { BaseMessage } from '~/utils/baseMessage'
import { websiteApi } from '@/api/website'
import { getAbsoluteUrl } from '@/utils'
import { EMPTY_VALUE_PLACEHOLDER } from '@/utils/constant'
import {
  canDownloadFile,
  downloadFileByUrl,
  formatFileSize,
  isVideoFile,
  resolveDownloadDisplayName,
  resolveDownloadFileName,
} from '@/utils/download'
import SharePopup from '~/components/base/SharePopup.vue'
import ContentResourcePreview from '~/components/website/service/ContentResourcePreview.vue'
import Feedback from '~/components/website/service/Feedback.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { resolveContentDetailResource } from '~/utils/website/contentDetailResource'
import { resolveLegacyServiceContentId } from '~/utils/website/legacyContentId'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'

definePageMeta({
  layout: 'website',
})

const route = useRoute()
const router = useRouter()
const shouldRefreshSsrContentSnapshot = import.meta.client && useNuxtApp().isHydrating
const { setSeo } = useSeo()
const { isMobile } = useMediaQuery()
const { withBase } = useBasePath()

setSeo({
  title: '下载详情',
  description: 'Tenda 下载中心资料详情与下载。',
})

const isDownloading = ref(false)
const reportedViewContentId = ref('')

/**
 * @description 标准化路径中的下载资料编号。
 * @returns {import('vue').ComputedRef<string>}
 */
const contentId = computed(() => resolveLegacyServiceContentId(route.params.id))

/**
 * @description 判断当前下载资料是否从帮助中心结果进入。
 * @returns {import('vue').ComputedRef<boolean>}
 */
const isFromHelpCenter = computed(() => {
  const source = Array.isArray(route.query.from) ? route.query.from[0] : route.query.from
  return source === 'help'
})

/**
 * @description 获取来源中心的面包屑名称。
 * @returns {import('vue').ComputedRef<string>}
 */
const sourceCenterLabel = computed(() => (isFromHelpCenter.value ? '帮助中心' : '下载中心'))

/**
 * @description 获取来源中心路由；从帮助中心进入时保留原列表筛选状态。
 * @returns {import('vue').ComputedRef<string|Object>}
 */
const sourceCenterRoute = computed(() => {
  if (!isFromHelpCenter.value) return '/website/service/download'

  const query = { ...route.query }
  delete query.from
  return {
    path: '/website/service/help',
    query: Object.keys(query).length > 0 ? query : undefined,
  }
})

if (!contentId.value) {
  throw createError({
    statusCode: 404,
    statusMessage: '下载资料不存在',
  })
}

const {
  data: rawContentDetail,
  pending,
  error,
  refresh,
} = await useAsyncData(
  () => `download-content-detail-${contentId.value}`,
  async () => {
    const response = import.meta.server
      ? await websiteApi.getContentDetail(contentId.value, createPublicContentSsrRequestConfig())
      : await websiteApi.getContentDetail(contentId.value)
    return response?.data || response || null
  },
  usePublicContentAsyncDataOptions({
    default: () => null,
    watch: [contentId],
  })
)

onMounted(() => {
  if (shouldRefreshSsrContentSnapshot) {
    refresh().catch(() => {})
  }
})

const contentDetail = computed(() => rawContentDetail.value || null)
const detailResource = computed(() => resolveContentDetailResource(contentDetail.value))
const detailTitle = computed(() => {
  const detail = contentDetail.value
  if (!detail) return '下载资料'
  return resolveDownloadDisplayName(detail) || detail.title || '下载资料'
})
const detailDate = computed(() => {
  const value = contentDetail.value?.updateTime || contentDetail.value?.createTime || ''
  return String(value).trim().split(/[T\s]/)[0]
})
const detailDownloadCount = computed(() => {
  return Number(contentDetail.value?.downloadCount || contentDetail.value?.downloadTimes || 0)
})
const detailPreviewCount = computed(() => {
  return Number(contentDetail.value?.previewCount || contentDetail.value?.previewTimes || 0)
})
const detailFileSize = computed(() => {
  const size = detailResource.value.fileSize || contentDetail.value?.fileSize
  return size ? formatFileSize(size) : '未知大小'
})
const detailStage = computed(() => {
  const detail = contentDetail.value || {}
  const values = Array.isArray(detail.applyStages)
    ? detail.applyStages
    : detail.applyStage
      ? [detail.applyStage]
      : []
  const labels = values
    .map((stage) => (Number(stage) === 1 ? '售前资料' : Number(stage) === 2 ? '售后资料' : ''))
    .filter(Boolean)
  return labels.join('、') || EMPTY_VALUE_PLACEHOLDER
})
const detailContentType = computed(() => {
  return (
    contentDetail.value?.contentFormName ||
    contentDetail.value?.contentCategoryName ||
    contentDetail.value?.contentTypeName ||
    EMPTY_VALUE_PLACEHOLDER
  )
})
const detailRichText = computed(() => {
  const detail = contentDetail.value
  if (!detail) return ''
  return detail.customizeContent || detail.content || detail.supplyNote || detail.description || ''
})
const isVideoResource = computed(() => isVideoFile(contentDetail.value))
const canDownloadResource = computed(() => {
  return detailResource.value.hasResource && canDownloadFile(contentDetail.value)
})
const shareUrl = computed(() => {
  const path = `/website/service/download/${encodeURIComponent(contentId.value)}`
  return getAbsoluteUrl(withBase(path))
})

/**
 * @description 上报下载中心内容统计，失败时不影响资料浏览或下载。
 * @param {number} statType - 1 预览量，2 下载量，3 分享量。
 * @returns {Promise<void>}
 */
const reportContentStat = async (statType) => {
  const id = contentDetail.value?.contentId || contentId.value
  if (!id) return

  try {
    await websiteApi.updateContentStat({ contentId: id, statType })
  } catch (error) {
    console.warn('下载资料统计上报失败', error)
  }
}

/**
 * @description 下载详情主资源。
 * @returns {Promise<void>}
 */
const handleDownload = async () => {
  if (!canDownloadResource.value || isDownloading.value) return

  isDownloading.value = true
  try {
    await refresh()
    const resource = detailResource.value
    const fileUrl = resource?.fileUrl || resource?.url || resource?.downloadUrl
    if (!fileUrl) throw new Error('File URL is missing')
    const result = await downloadFileByUrl({
      url: fileUrl,
      filename: resolveDownloadFileName(resource),
      fileType: resource.fileType,
      contentForm: resource.contentForm,
    })
    BaseMessage.success(result?.strategy === 'native' ? '已开始下载' : '保存成功')
    reportContentStat(2)

    if (rawContentDetail.value) {
      rawContentDetail.value = {
        ...rawContentDetail.value,
        downloadCount: detailDownloadCount.value + 1,
      }
    }
  } catch (error) {
    BaseMessage.error('下载失败，请稍后重试')
  } finally {
    isDownloading.value = false
  }
}

const handleBackToSourceCenter = () => {
  router.push(sourceCenterRoute.value)
}

watchEffect(() => {
  const detail = contentDetail.value
  if (!detail) return

  setSeo({
    title: `${detailTitle.value} - ${sourceCenterLabel.value}`,
    description: detail.abstractText || detail.abstract || detailTitle.value,
  })
})

watchEffect(() => {
  const id = contentDetail.value?.contentId || contentId.value
  if (!id || reportedViewContentId.value === id) return

  reportedViewContentId.value = id
  reportContentStat(1)
})
</script>

<template>
  <main class="download-detail-page">
    <div class="base-container">
      <section
        v-if="pending && !contentDetail"
        class="download-detail-page__loading"
        aria-live="polite"
      >
        <a-skeleton :animation="true">
          <a-space direction="vertical" :style="{ width: '100%' }" size="large">
            <a-skeleton-line :rows="1" :widths="['20%']" />
            <a-skeleton-line :rows="1" :widths="['40%']" />
            <a-skeleton-shape shape="rectangle" :style="{ width: '100%', height: '600px' }" />
          </a-space>
        </a-skeleton>
      </section>

      <section v-else-if="error" class="download-detail-page__error">
        <a-result status="error" title="资料详情加载失败">
          <template #extra>
            <a-space>
              <a-button type="primary" @click="refresh">重试</a-button>
              <a-button @click="handleBackToSourceCenter">返回{{ sourceCenterLabel }}</a-button>
            </a-space>
          </template>
        </a-result>
      </section>

      <template v-else-if="contentDetail">
        <nav class="download-detail-page__breadcrumb" aria-label="面包屑">
          <NuxtLink to="/website/service" class="download-detail-page__breadcrumb-item">
            服务
          </NuxtLink>
          <span class="download-detail-page__breadcrumb-separator">/</span>
          <NuxtLink :to="sourceCenterRoute" class="download-detail-page__breadcrumb-item">
            {{ sourceCenterLabel }}
          </NuxtLink>
          <span class="download-detail-page__breadcrumb-separator">/</span>
          <span class="download-detail-page__breadcrumb-item--active">资料详情</span>
        </nav>

        <article class="download-detail-page__article">
          <header class="download-detail-page__header">
            <div class="download-detail-page__header-main">
              <h1 class="download-detail-page__title">{{ detailTitle }}</h1>
              <div class="download-detail-page__meta-row">
                <span class="download-detail-page__meta-item">
                  <span class="download-detail-page__meta-label">适用阶段：</span>
                  <span>{{ detailStage }}</span>
                </span>
                <span class="download-detail-page__meta-item">
                  <span class="download-detail-page__meta-label">内容类型：</span>
                  <span>{{ detailContentType }}</span>
                </span>
                <span class="download-detail-page__stats">
                  <span v-if="detailDate" class="download-detail-page__stat-item">
                    <icon-clock-circle />{{ detailDate }}
                  </span>
                  <span class="download-detail-page__stat-item">
                    <template v-if="isVideoResource">
                      <icon-eye />{{ detailPreviewCount }}
                    </template>
                    <template v-else> <icon-download />{{ detailDownloadCount }} </template>
                  </span>
                  <span class="download-detail-page__stat-item">{{ detailFileSize }}</span>
                </span>
              </div>
            </div>

            <div class="download-detail-page__actions">
              <a-button
                v-if="canDownloadResource"
                class="download-detail-page__action-button"
                :loading="isDownloading"
                :size="isMobile ? 'small' : 'large'"
                @click="handleDownload"
              >
                <template #icon><icon-download /></template>
                下载
              </a-button>
              <SharePopup
                :share-url="shareUrl"
                :share-title="detailTitle"
                @share="reportContentStat(3)"
              >
                <template #trigger>
                  <a-button
                    class="download-detail-page__action-button"
                    :size="isMobile ? 'small' : 'large'"
                  >
                    <template #icon><icon-share-internal /></template>
                    分享
                  </a-button>
                </template>
              </SharePopup>
            </div>
          </header>

          <div class="download-detail-page__content">
            <ContentResourcePreview
              v-if="detailResource.hasResource"
              :resource="detailResource"
              :downloading="isDownloading"
              :show-download-action="false"
              class="download-detail-page__preview"
              @download="handleDownload"
              @refresh-required="refresh"
            />
            <div v-if="detailRichText" class="download-detail-page__rich-text">
              <BaseRichTextPreview :html-content="detailRichText" />
            </div>
            <div
              v-else-if="!detailResource.hasResource"
              class="download-detail-page__empty-content"
            >
              暂无内容
            </div>
          </div>

          <Feedback class="download-detail-page__feedback" :content-id="contentId" />
        </article>
      </template>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.download-detail-page {
  min-height: calc(100vh - 160px);
  padding: 20px 0 64px;
  background-color: $bg-white;

  &__loading,
  &__error {
    min-height: 500px;
    padding: 60px;
    border-radius: 12px;
    background: $bg-white;
  }

  &__breadcrumb {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 20px;
    color: $text-content-color;
    font-size: 14px;
    line-height: 22px;
  }

  &__breadcrumb-item {
    color: $text-content-color;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: $primary-color;
    }

    &--active {
      color: $text-title-color;
      pointer-events: none;
    }
  }

  &__breadcrumb-separator {
    margin: 0 4px;
    color: $border-hover-color;
  }

  &__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 40px;
    padding-bottom: 24px;
    border-bottom: 1px solid $border-light-color;
  }

  &__header-main {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0 0 16px;
    color: $text-title-color;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.25;
    word-break: break-word;
  }

  &__meta-row,
  &__stats,
  &__meta-item,
  &__stat-item,
  &__actions {
    display: flex;
    align-items: center;
  }

  &__meta-row {
    flex-wrap: wrap;
    gap: 24px;
    color: $text-stat-zero-color;
    font-size: 14px;
    line-height: 22px;
  }

  &__meta-label {
    white-space: nowrap;
  }

  &__stats {
    gap: 12px;
  }

  &__stat-item {
    gap: 4px;

    :deep(.arco-icon) {
      font-size: 14px;
    }
  }

  &__actions {
    flex-shrink: 0;
    gap: 12px;
  }

  &__action-button {
    display: flex;
    align-items: center;
    width: 82px;
    height: 40px;
    padding: 0 16px;
    border: 1px solid $border-light-color;
    border-radius: $radius-6;
    background-color: $bg-white;
    color: $text-title-color;
    font-size: 14px;
    transition: all 0.3s;

    &:hover {
      border-color: $primary-color;
      background-color: $bg-white;
      color: $primary-color;
    }

    :deep(.arco-icon) {
      display: inline-flex;
      font-size: 16px;
    }
  }

  &__content {
    padding-top: 24px;
  }

  &__preview {
    width: 100%;
  }

  &__rich-text {
    margin-top: 24px;

    :deep(a[id][name]) {
      scroll-margin-top: calc(#{$base-header-height} + #{$spacing-md});
    }
  }

  &__empty-content {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    border: 1px solid $border-light-color;
    border-radius: $radius-6;
    color: $text-subtitle-color;
    font-size: 14px;
    line-height: 22px;
  }

  &__feedback {
    margin-top: 60px;
  }
}

@include respond-to-max('lg') {
  .download-detail-page {
    min-height: calc(100vh - 48px);
    padding: 16px 0 32px;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
    }

    &__title {
      font-size: 24px;
    }

    &__meta-row {
      gap: 12px;
    }

    &__actions {
      width: 100%;
    }

    &__action-button {
      width: 82px;
      height: 40px;
      padding: 0 16px;
      font-size: 14px;

      :deep(.arco-icon) {
        font-size: 16px;
      }
    }

    &__content {
      padding-top: 16px;
    }

    &__rich-text {
      margin-top: 16px;
    }

    &__feedback {
      margin-top: 40px;
    }
  }
}

@include respond-to-max('md') {
  .download-detail-page {
    padding-bottom: 0;

    &__loading,
    &__error {
      min-height: 320px;
      padding: 32px 16px;
    }

    &__breadcrumb {
      margin-bottom: 16px;
    }
  }
}
</style>

<style lang="scss">
body:has(.download-detail-page) {
  .base-footer {
    border-top: 1px solid #e5e6eb;
  }
}
</style>
